import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import { Button } from '@/atoms/Button';
import { Chip } from '@/atoms/Chip';
import { Search } from '@/molecules/Search';
import { DataTable, type MRT_ColumnDef } from '@/organisms/Table';
import { EmptyState } from '@/organisms/EmptyState';
import { TablePageLayout } from '@/templates/TablePageLayout';
import SearchOffIcon from '@mui/icons-material/SearchOff';

/**
 * TableExample
 * Example list page: TablePageLayout + Search toolbar + filterable DataTable,
 * with an EmptyState when the query matches nothing.
 */
interface Asset {
  name: string;
  type: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  region: string;
  owner: string;
}

const allAssets: Asset[] = [
  { name: 'prod-db-01', type: 'RDS', severity: 'critical', region: 'us-east-1', owner: 'platform' },
  { name: 'api-gateway', type: 'ALB', severity: 'high', region: 'us-east-1', owner: 'api' },
  { name: 's3-backups', type: 'S3', severity: 'medium', region: 'us-west-2', owner: 'data' },
  { name: 'iam-role-ci', type: 'IAM', severity: 'low', region: 'global', owner: 'devops' },
  { name: 'eks-cluster', type: 'EKS', severity: 'high', region: 'eu-west-1', owner: 'platform' },
];

export function TableExample() {
  const [query, setQuery] = useState('');
  const data = useMemo(
    () => allAssets.filter((a) => a.name.toLowerCase().includes(query.toLowerCase())),
    [query],
  );
  const columns = useMemo<MRT_ColumnDef<Asset>[]>(
    () => [
      { accessorKey: 'name', header: 'Asset' },
      { accessorKey: 'type', header: 'Type' },
      {
        accessorKey: 'severity',
        header: 'Severity',
        Cell: ({ cell }) => (
          <Chip purpose="severity" level={cell.getValue<Asset['severity']>()} size="small" />
        ),
      },
      { accessorKey: 'region', header: 'Region' },
      { accessorKey: 'owner', header: 'Owner' },
    ],
    [],
  );

  return (
    <Box sx={{ p: 3 }}>
      <TablePageLayout
        title="Inventory"
        description="All cloud assets discovered across connected accounts."
        actions={<Button startIcon={<AddIcon />}>Add account</Button>}
        toolbar={
          <Box sx={{ width: 320 }}>
            <Search placeholder="Search assets..." onSearch={setQuery} />
          </Box>
        }
      >
        {data.length > 0 ? (
          <DataTable columns={columns} data={data} enableRowSelection />
        ) : (
          <EmptyState
            icon={<SearchOffIcon fontSize="inherit" />}
            title="No assets match your search"
            description={`Nothing found for "${query}". Try a different term.`}
            action={<Button variant="secondary" onClick={() => setQuery('')}>Clear search</Button>}
          />
        )}
      </TablePageLayout>
    </Box>
  );
}
